/**
 * Funzione accessoria di HSL2RGB, uso interno
 *
 * @param p
 * @param q
 * @param t
 */
function hue2rgb(p, q, t) {
    if (t < 0) {
        t += 1;
    }
    if (t > 1) {
        t -= 1;
    }
    if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
        return q;
    }
    if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
}
/**
 * Traduce Hue, Saturation e Light in notazione RGB per CSS
 *
 */
export function HSL2RGB(hsl) {
    var h = hsl.h, s = hsl.s, l = hsl.l;
    var r;
    var g;
    var b;
    if (s === 0) {
        r = g = b = l; // achromatic
    }
    else {
        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    return {
        r: r * 255,
        g: g * 255,
        b: b * 255,
    };
}
/**
 * Prende un RGB e lo formatta per essere utilizzato nel CSS
 *
 * @return string "rgb([0-255], [0-255], [0-255])"
 */
export function formatRGB(rgb) {
    var r = rgb.r, g = rgb.g, b = rgb.b, a = rgb.a;
    return "rgb(" + Math.floor(r) + ", " + Math.floor(g) + ", " + Math.floor(b) + (a !== undefined ? ', ' + a + '' : '') + ")";
}
/**
 *
 */
export function RGB2HSL(rgb) {
    var r = rgb.r, g = rgb.g, b = rgb.b;
    r /= 255;
    g /= 255;
    b /= 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var l = (max + min) / 2;
    var h;
    var s;
    if (max === min) {
        h = s = 0; // achromatic
    }
    else {
        var d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    return {
        h: h,
        s: s,
        l: l,
    };
}
/**
 * Utility che dati i possibili valori di HSL, con S e L coniugati in 3 varianti di luminosità,
 *
 * @param hue
 * @param ligths
 * @param saturations
 * @returns IColor
 */
export function defaultColor(hue, ligths, saturations) {
    if (ligths === void 0) { ligths = {}; }
    if (saturations === void 0) { saturations = {}; }
    var h = hue / 360;
    return {
        plain: formatRGB(HSL2RGB({ h: h, s: saturations.plain || 0, l: ligths.plain || 0 })),
        light: formatRGB(HSL2RGB({ h: h, s: saturations.plain || 0, l: ligths.light || 0 })),
        dark: formatRGB(HSL2RGB({ h: h, s: saturations.plain || 0, l: ligths.dark || 0 })),
        disabled: {
            plain: formatRGB(HSL2RGB({ h: h, s: saturations.disabled || 0, l: ligths.plain || 0 })),
            light: formatRGB(HSL2RGB({ h: h, s: saturations.disabled || 0, l: ligths.light || 0 })),
            dark: formatRGB(HSL2RGB({ h: h, s: saturations.disabled || 0, l: ligths.dark || 0 })),
        },
    };
}
export function themeGenerator(input) {
    var sa = 1.616; // Golden ratio
    var primary = defaultColor(input.primaryHue, { dark: input.dark, plain: 0.52, light: input.light }, { plain: 0.85, disabled: input.disabledSaturation });
    var secondary = defaultColor(input.secondaryHue, { dark: input.dark, plain: 0.33, light: input.light }, { plain: 1, disabled: input.disabledSaturation });
    var alt = defaultColor(input.altHue, { dark: input.dark, plain: input.plain, light: input.light }, { plain: 1, disabled: input.disabledSaturation });
    var alt2 = defaultColor(input.alt2Hue, { dark: input.dark, plain: input.plain, light: input.light }, { plain: 1, disabled: input.disabledSaturation });
    var grey = defaultColor(input.greyHue, { dark: input.dark, plain: input.plain, light: input.light }, { plain: 0, disabled: 0 });
    return {
        colors: {
            primary: primary,
            secondary: secondary,
            alt: alt,
            alt2: alt2,
            grey: grey,
            danger: primary,
            success: secondary,
            info: alt,
            premium: alt2,
            action: grey,
            ink: input.ink,
            inkWhite: input.inkWhite,
            white: input.white,
        },
        ratios: {
            l: 1 + 1 / Math.pow(sa, 3),
            xl: sa,
            s: 1 / (1 + 1 / Math.pow(sa, 3)),
            xs: 1 / sa,
        },
        font: {
            family: "\"Montserrat\", sans-serif;",
            baseSize: 20,
            weights: {
                light: 300,
                regular: 400,
                semibold: 600,
                bold: 700,
            },
        },
        radius: 1 / Math.pow(sa, 3),
        baseTiming: input.baseTiming,
        transition: function (property, timingFactor, easing) {
            if (timingFactor === void 0) { timingFactor = 1; }
            if (easing === void 0) { easing = 'cubic-bezier(.4,0,.2,1)'; }
            return property + " " + input.baseTiming * timingFactor + "s " + easing;
        },
        boxShadow: function (level) {
            if (level === void 0) { level = 1; }
            return "0 0 " + input.ambientShadowDiffusion * level + "rem 0 " + input.ambientShadowColor + ", " + input.directShadowX * level + "rem " + input.directShadowY * level + "rem " + input.directShadowDiffusion * level + "rem 0 " + input.directShadowColor;
        },
        // `0 ${0.7 * level}rem ${1.4 * level}rem rgba(0,0,0,.25), 0 ${0.5 *
        // 	level}rem ${0.5 * level}rem rgba(0,0,0,.22)`,
        dimmerOpacity: input.dimmerOpacity,
        timings: {
            base: 500,
        },
        breakpoints: {
            mobileMax: 600,
            tabletMax: 900,
            notebookMax: 1200,
        },
        zIndexes: {
            P1_SlightlyAbove: 10,
            P2_AreaAbove: 100,
            P3_ContextualPopup: 1000,
            P4_Modal: 10000,
            P5_TopModal: 100000,
        },
        button: {
            fontWeight: 400,
        },
    };
}
export var defaultTheme = themeGenerator({
    primaryHue: 358,
    secondaryHue: 149,
    altHue: 205,
    alt2Hue: 52,
    greyHue: 0,
    disabledSaturation: 0.3,
    ink: '#212121',
    white: '#ffffff',
    inkWhite: '#ededed',
    light: 0.95,
    plain: 0.6,
    dark: 0.3,
    dimmerOpacity: 0.5,
    baseTiming: 0.28,
    directShadowX: 0.2,
    directShadowY: 0.4,
    directShadowDiffusion: 0.4,
    ambientShadowDiffusion: 1.1,
    directShadowColor: 'rgba(0,0,0,.15)',
    ambientShadowColor: 'rgba(0,0,0,.15)',
});
//# sourceMappingURL=theme.js.map